type BridgeAction = "setRefresh" | "getRefresh" | "clearRefresh";

type BridgePayloadMap = {
  setRefresh: { refreshToken: string; username: string };
  getRefresh: { refreshToken: string; username: string } | null;
  clearRefresh: boolean;
};

function resolveBridgeUrl(): string {
  const metaEnv =
    typeof import.meta !== "undefined" && (import.meta as any)?.env
      ? (import.meta as any).env
      : undefined;

  return (
    metaEnv?.VITE_BRIDGE_URL ??
    (process.env.REACT_APP_BRIDGE_URL as string | undefined) ??
    (process.env.VITE_BRIDGE_URL as string | undefined) ??
    ""
  );
}

function ensureDocumentReady(): Promise<void> {
  if (document.readyState === "loading") {
    return new Promise((resolve) => {
      document.addEventListener("DOMContentLoaded", () => resolve(), {
        once: true,
      });
    });
  }
  return Promise.resolve();
}

function talk<T extends BridgeAction>(
  type: T,
  payload?: BridgePayloadMap[T],
): Promise<BridgePayloadMap[T]> {
  const bridgeUrl = resolveBridgeUrl();

  console.log("[Bridge] Attempting to communicate with bridge:", bridgeUrl);

  if (!bridgeUrl) {
    return Promise.reject(new Error("Bridge URL is not configured"));
  }

  return new Promise(async (resolve, reject) => {
    try {
      await ensureDocumentReady();

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = bridgeUrl;

      const origin = new URL(bridgeUrl).origin;
      console.log("[Bridge] Bridge origin:", origin);
      console.log("[Bridge] Current window origin:", window.location.origin);

      let timeoutId: number | undefined;

      const cleanup = () => {
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }
        window.removeEventListener("message", handleMessage);
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
      };

      const handleMessage = (event: MessageEvent) => {
        console.log(
          "[Bridge] Received message:",
          event.data,
          "from origin:",
          event.origin,
        );
        if (event.source !== iframe.contentWindow) {
          console.log("[Bridge] Message source mismatch, ignoring");
          return;
        }
        if (event.data?.type === `${type}:ok`) {
          console.log("[Bridge] Received expected response");
          cleanup();
          resolve(event.data.payload as BridgePayloadMap[T]);
        }
      };

      window.addEventListener("message", handleMessage);

      iframe.onload = () => {
        console.log("[Bridge] Iframe loaded successfully");

        // Add a small delay to ensure the iframe is fully ready
        setTimeout(() => {
          try {
            if (!iframe.contentWindow) {
              console.error("[Bridge] Iframe contentWindow is null");
              cleanup();
              reject(new Error("Iframe contentWindow is not accessible"));
              return;
            }

            console.log(
              "[Bridge] Iframe contentWindow available:",
              !!iframe.contentWindow,
            );
            console.log("[Bridge] Iframe src:", iframe.src);
            console.log(
              "[Bridge] Iframe ready state:",
              iframe.contentDocument?.readyState,
            );

            const messageToSend = { type, payload };
            console.log(
              "[Bridge] Sending message:",
              messageToSend,
              "to origin:",
              origin,
            );

            // Try sending with both the specific origin and '*' to debug
            try {
              iframe.contentWindow.postMessage(messageToSend, origin);
              console.log("[Bridge] Message sent to specific origin:", origin);
            } catch (specificErr) {
              console.error(
                "[Bridge] Failed to send to specific origin:",
                specificErr,
              );
              // Fallback to wildcard origin
              iframe.contentWindow.postMessage(messageToSend, "*");
              console.log("[Bridge] Message sent to wildcard origin");
            }

            timeoutId = window.setTimeout(() => {
              console.log("[Bridge] Timeout reached, no response received");
              console.log(
                "[Bridge] Final iframe state - contentWindow:",
                !!iframe.contentWindow,
              );
              console.log("[Bridge] Final iframe state - src:", iframe.src);
              cleanup();
              reject(
                new Error(
                  "Bridge did not respond in time. Ensure sso.html is deployed and the origin is allowlisted for this environment.",
                ),
              );
            }, 10000); // Increased timeout to 10 seconds
          } catch (err) {
            console.error("[Bridge] Error sending message:", err);
            cleanup();
            reject(err);
          }
        }, 500); // Increased delay to 500ms to ensure iframe is ready
      };

      iframe.onerror = (error) => {
        console.error("[Bridge] Iframe failed to load:", error);
        cleanup();
        reject(new Error("Failed to load bridge iframe"));
      };

      console.log("[Bridge] Appending iframe to document");
      document.body.appendChild(iframe);
    } catch (error) {
      console.error("[Bridge] Unexpected error:", error);
      reject(error);
    }
  });
}

export const setRefresh = (payload: {
  refreshToken: string;
  username: string;
}) => talk("setRefresh", payload);
export const getRefresh = () => talk("getRefresh");
export const clearRefresh = () => talk("clearRefresh");
